import * as React from "react";
import { Link, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Send } from "lucide-react";
import { useLocalizedHref } from "@/hooks/useLocalizedHref";

const PRODUCT_VALUES = ["audit", "core", "premium"] as const;

export function ContactPage() {
  const { t } = useTranslation();
  const localizedHref = useLocalizedHref();
  // strict:false — this page is mounted at both /contact and /fr/contact
  // (route id "/$lang/contact"); a strict from="/contact" throws an
  // invariant on the French route.
  const { plan } = useSearch({ strict: false }) as { plan?: string };
  const [formState, setFormState] = React.useState({
    email: "",
    name: "",
    productType: plan || "",
    description: "",
  });
  const [submitted, setSubmitted] = React.useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: wire up to backend/email service
    setSubmitted(true);
  };

  const charsLeft = 500 - formState.description.length;

  if (submitted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="w-full max-w-md text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full border border-primary/20 bg-primary/10">
            <Send className="h-7 w-7 text-primary" />
          </div>
          <h1 className="mb-4 text-3xl font-extrabold text-foreground">
            {t("contact.success.title")}
          </h1>
          <p className="mb-8 text-muted-foreground">
            {t("contact.success.body", { name: formState.name })}
          </p>
          <Link to={localizedHref("/")}>
            <Button className="hover:bg-primary-hover bg-primary text-primary-foreground">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t("contact.backToHome")}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-20">
      <div className="w-full max-w-lg">
        {/* Back link */}
        <Link
          to={localizedHref("/")}
          className="mb-8 inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("contact.backToHome")}
        </Link>

        {/* Header */}
        <h1 className="mb-2 text-3xl font-extrabold text-foreground sm:text-4xl">
          {t("contact.title")}
        </h1>
        <p className="mb-8 text-muted-foreground">{t("contact.lede")}</p>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Name */}
          <div>
            <label htmlFor="name" className="mb-2 block text-sm font-semibold text-foreground">
              {t("contact.labels.name")}
            </label>
            <input
              id="name"
              type="text"
              required
              value={formState.name}
              onChange={(e) => setFormState((s) => ({ ...s, name: e.target.value }))}
              className={cn(
                "w-full rounded-lg px-4 py-3",
                "border border-border bg-muted",
                "text-foreground placeholder:text-muted-foreground",
                "focus:border-transparent focus:ring-2 focus:ring-primary focus:outline-none",
                "transition-all duration-200",
              )}
              placeholder={t("contact.placeholders.name")}
            />
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="mb-2 block text-sm font-semibold text-foreground">
              {t("contact.labels.email")}
            </label>
            <input
              id="email"
              type="email"
              required
              value={formState.email}
              onChange={(e) => setFormState((s) => ({ ...s, email: e.target.value }))}
              className={cn(
                "w-full rounded-lg px-4 py-3",
                "border border-border bg-muted",
                "text-foreground placeholder:text-muted-foreground",
                "focus:border-transparent focus:ring-2 focus:ring-primary focus:outline-none",
                "transition-all duration-200",
              )}
              placeholder={t("contact.placeholders.email")}
            />
          </div>

          {/* Product type */}
          <div>
            <label
              htmlFor="productType"
              className="mb-2 block text-sm font-semibold text-foreground"
            >
              {t("contact.labels.productType")}
            </label>
            <select
              id="productType"
              required
              value={formState.productType}
              onChange={(e) => setFormState((s) => ({ ...s, productType: e.target.value }))}
              className={cn(
                "w-full appearance-none rounded-lg px-4 py-3",
                "border border-border bg-muted",
                "text-foreground",
                "focus:border-transparent focus:ring-2 focus:ring-primary focus:outline-none",
                "transition-all duration-200",
                !formState.productType && "text-muted-foreground",
              )}
            >
              <option value="" disabled>
                {t("contact.placeholders.productType")}
              </option>
              {PRODUCT_VALUES.map((value) => (
                <option key={value} value={value}>
                  {t(`contact.products.${value}`)}
                </option>
              ))}
            </select>
            <p className="mt-1.5 text-xs text-muted-foreground">
              {t("contact.notSure")}{" "}
              <Link
                to={localizedHref("/")}
                hash="pricing"
                className="text-primary hover:underline"
                onClick={() => {
                  setTimeout(() => {
                    document.querySelector("#pricing")?.scrollIntoView({ behavior: "smooth" });
                  }, 100);
                }}
              >
                {t("contact.comparePlans")}
              </Link>
            </p>
          </div>

          {/* Description */}
          <div>
            <label
              htmlFor="description"
              className="mb-2 block text-sm font-semibold text-foreground"
            >
              {t("contact.labels.description")}
            </label>
            <textarea
              id="description"
              required
              maxLength={500}
              rows={4}
              value={formState.description}
              onChange={(e) => setFormState((s) => ({ ...s, description: e.target.value }))}
              className={cn(
                "w-full resize-none rounded-lg px-4 py-3",
                "border border-border bg-muted",
                "text-foreground placeholder:text-muted-foreground",
                "focus:border-transparent focus:ring-2 focus:ring-primary focus:outline-none",
                "transition-all duration-200",
              )}
              placeholder={t("contact.placeholders.description")}
            />
            <p
              className={cn(
                "mt-1.5 text-right text-xs",
                charsLeft < 50 ? "text-destructive" : "text-muted-foreground",
              )}
            >
              {t("contact.charsRemaining", { count: charsLeft })}
            </p>
          </div>

          {/* Submit */}
          <Button
            type="submit"
            size="lg"
            className="hover:bg-primary-hover w-full bg-primary text-primary-foreground transition-all duration-200 hover:scale-[1.02]"
          >
            {t("contact.submit")}
            <Send className="ml-2 h-4 w-4" />
          </Button>
        </form>
      </div>
    </div>
  );
}
